import { Fragment, useMemo } from "react";

import { useIntl } from "react-intl";

import { ConfluenceLogo } from "@atlaskit/logo";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint/use-touchpoint/index";
import { ConfluenceSmartBanner } from "@/components/smart-banner/src/confluence-smart-banner";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { LogoV3 } from "@/libraries/wpl/foundations/src/logo-v3";
import { DropdownContent } from "@/patterns/global-nav/src/dropdown-content";
import { GlobalNavLinkContentProps } from "@/patterns/global-nav/src/nav-links-container";
import { ProductPageSubNav } from "@/patterns/global-nav/src/nav-product-page";
import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

const ConfluenceLogoComponent = () => {
    const gwp7518NavIconUpdates = checkFeatureFlagGate(
        featureFlags.gwp7518NavIconUpdates
    );

    return gwp7518NavIconUpdates ? (
        <LogoV3 brand="confluence" type="logo" appearance="brand" />
    ) : (
        <ConfluenceLogo appearance="brand" />
    );
};

const ConfluenceSoftwareNav = () => {
    const intl = useIntl();

    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=confluence&edition=free"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.CONFLUENCE,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });

    const templatesDropdown = useMemo(
        () => (
            <DropdownContent
                contentSections={[
                    {
                        links: [
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.allTemplates",
                                    defaultMessage: "All templates"
                                }),
                                href: "/software/confluence/templates"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.design",
                                    defaultMessage: "Design"
                                }),
                                href: "/software/confluence/templates/categories/design"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.financeAndOperations",
                                    defaultMessage: "Finance & Ops"
                                }),
                                href: "/software/confluence/templates/categories/finance-and-operations"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.humanResources",
                                    defaultMessage: "Human Resources"
                                }),
                                href: "/software/confluence/templates/categories/human-resources"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.marketingAndSales",
                                    defaultMessage: "Marketing & Sales"
                                }),
                                href: "/software/confluence/templates/categories/marketing-and-sales"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.productManagement",
                                    defaultMessage: "Product Management"
                                }),
                                href: "/software/confluence/templates/categories/product-management"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.projectManagement",
                                    defaultMessage: "Project Management"
                                }),
                                href: "/software/confluence/templates/categories/project-management"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.recommended",
                                    defaultMessage: "Recommended"
                                }),
                                href: "/software/confluence/templates/categories/recommended"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "confluence.TemplateCategoriesDropdown.softwareDevelopmentAndIt",
                                    defaultMessage: "Software & IT"
                                }),
                                href: "/software/confluence/templates/categories/software-development-and-it"
                            }
                        ]
                    }
                ]}
            />
        ),
        [intl]
    );
    const featuresDropdown = useMemo(
        () => (
            <DropdownContent
                contentSections={[
                    {
                        links: [
                            {
                                text: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.features.allFeatures",
                                    defaultMessage: "All Features"
                                }),
                                href: "/software/confluence/features"
                            },
                            {
                                text: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.features.rovoInConfluence",
                                    defaultMessage: "Rovo in Confluence"
                                }),
                                href: "/software/confluence/ai"
                            }
                        ]
                    }
                ]}
            />
        ),
        [intl]
    );

    const templatesLinkContent = useMemo<GlobalNavLinkContentProps>(() => {
        const common = {
            globalLinksKey: "templates",
            linkText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.navbar.templates",
                defaultMessage: "Templates"
            }),
            productKey: "confluence"
        };

        return {
            ...common,
            hasSubnav: true,
            // TODO: replace with TemplateCategoriesDropdown to have dynamic categories
            content: templatesDropdown,
            dropdownType: "templates",
            containerPosition: "relative"
        };
    }, [intl, templatesDropdown]);

    const productNavGoToAppCta = useExperiment(
        experiments.productNavGoToAppCta
    );
    const isExperimentEnabled =
        productNavGoToAppCta.type === "enrolled" &&
        productNavGoToAppCta.value === true;
    const ctaText = isExperimentEnabled
        ? intl.formatMessage({
              id: "ConfluenceSoftwarePageTemplate.navbar.openConfluence",
              defaultMessage: "Open Confluence"
          })
        : intl.formatMessage({
              id: "ConfluenceSoftwarePageTemplate.navbar.getItFree",
              defaultMessage: "Get it free"
          });

    return (
        <Fragment>
            <ConfluenceSmartBanner />
            <UFOSegment name="confluence-pt-cfind-1234-confluenceSoftwareNav">
                <ProductPageSubNav
                    productLogoUrl={useLocalizedHref("/software/confluence")}
                    productLogo={<ConfluenceLogoComponent />}
                    productLogoAriaLabel={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.navbar.confluence-logo",
                        defaultMessage: "Confluence logo"
                    })}
                    mobileCtaText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.navbar.getitfree",
                        defaultMessage: "Get it free"
                    })}
                    navigationTrynowCtaText={ctaText}
                    mobileCtaUrl={signupLink}
                    mobileCtaOnClick={openCrossFlow}
                    mobileLogo={<ConfluenceLogoComponent />}
                    productPageNavLinks={[
                        {
                            globalLinksKey: "features",
                            linkText: intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.navbar.features",
                                defaultMessage: "Features"
                            }),
                            hasSubnav: true,
                            content: featuresDropdown,
                            productKey: "confluence",
                            containerPosition: "relative"
                        },
                        {
                            globalLinksKey: "resources",
                            linkText: intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.navbar.resources",
                                defaultMessage: "Resources"
                            }),
                            hasSubnav: false,
                            productKey: "confluence",
                            href: useLocalizedHref(
                                "/software/confluence/resources"
                            )
                        },
                        templatesLinkContent,
                        {
                            globalLinksKey: "pricing",
                            linkText: intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.navbar.pricing",
                                defaultMessage: "Pricing"
                            }),
                            hasSubnav: false,
                            productKey: "confluence",
                            href: useLocalizedHref(
                                "/software/confluence/pricing"
                            )
                        },
                        {
                            globalLinksKey: "enterprise",
                            linkText: intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.navbar.enterprise",
                                defaultMessage: "Enterprise"
                            }),
                            hasSubnav: false,
                            productKey: "confluence",
                            href: useLocalizedHref(
                                "/software/confluence/enterprise"
                            )
                        }
                    ]}
                    navigationTrynowCtaUrl={signupLink}
                    navigationTrynowCtaOnClick={openCrossFlow}
                    mobileLinksSections={[
                        {
                            mobileLinkKey: "mobile-confluence",
                            mobileLinksProps: [
                                {
                                    globalLinksKey: "features",
                                    linkText: intl.formatMessage({
                                        id: "ConfluenceSoftwarePageTemplate.navbar.features",
                                        defaultMessage: "Features"
                                    }),
                                    hasSubnav: false,
                                    productKey: "confluence",
                                    href: useLocalizedHref(
                                        "/software/confluence/features"
                                    )
                                },
                                {
                                    globalLinksKey: "resources",
                                    linkText: intl.formatMessage({
                                        id: "ConfluenceSoftwarePageTemplate.navbar.resources",
                                        defaultMessage: "Resources"
                                    }),
                                    hasSubnav: false,
                                    productKey: "confluence",
                                    href: useLocalizedHref(
                                        "/software/confluence/resources"
                                    )
                                },
                                templatesLinkContent,
                                {
                                    globalLinksKey: "pricing",
                                    linkText: intl.formatMessage({
                                        id: "ConfluenceSoftwarePageTemplate.navbar.pricing",
                                        defaultMessage: "Pricing"
                                    }),
                                    hasSubnav: false,
                                    productKey: "confluence",
                                    href: useLocalizedHref(
                                        "/software/confluence/pricing"
                                    )
                                },
                                {
                                    globalLinksKey: "enterprise",
                                    linkText: intl.formatMessage({
                                        id: "ConfluenceSoftwarePageTemplate.navbar.enterprise",
                                        defaultMessage: "Enterprise"
                                    }),
                                    hasSubnav: false,
                                    productKey: "confluence",
                                    href: useLocalizedHref(
                                        "/software/confluence/enterprise"
                                    )
                                }
                            ]
                        }
                    ]}
                    additionalMenuPageObjectsArray={[
                        {
                            menuPageKey: "templates",
                            menuPageObjects:
                                templatesLinkContent?.content as JSX.Element
                        }
                    ]}
                />
            </UFOSegment>
        </Fragment>
    );
};

ConfluenceSoftwareNav.displayName = "ConfluenceSoftwareNav";

export default ConfluenceSoftwareNav;
